export const sanitizeE911Field = value => {
  const text = `${value ?? ''}`.trim();
  if (!text) return '';
  const normalized = text.toLowerCase();
  if (
    ['not specified', 'n/a', 'na', 'null', 'undefined', '-'].includes(normalized)
  ) {
    return '';
  }
  return text;
};

const firstValid = (...values) => {
  for (const value of values) {
    const cleaned = sanitizeE911Field(value);
    if (cleaned) return cleaned;
  }
  return '';
};

export const getPrefillE911Address = (box, dn = '') => {
  const source = box || {};
  const locAddress =
    source?.locRef?.address && typeof source.locRef.address === 'object'
      ? source.locRef.address
      : source?.locRef || {};
  const shipped = source?.shippedAddress || {};
  const existing = source?.e911Info || {};

  return {
    callername: firstValid(
      existing?.callername,
      shipped?.attn,
      source?.assignedTo?.name,
      source?.locRef?.locationName,
    ),
    address1: firstValid(
      locAddress?.address,
      locAddress?.address1,
      locAddress?.street1,
      existing?.address1,
      shipped?.street1,
    ),
    address2: firstValid(
      locAddress?.address2,
      locAddress?.street2,
      existing?.address2,
      shipped?.street2,
    ),
    city: firstValid(locAddress?.city, existing?.city, shipped?.city),
    state: firstValid(locAddress?.state, existing?.state, shipped?.state),
    zip: firstValid(locAddress?.zip, existing?.zip, shipped?.zip),
    country: firstValid(
      locAddress?.country,
      existing?.country,
      shipped?.country,
      'United States',
    ),
    dn,
  };
};

export const formatE911AddressLines = address => {
  if (!address) return [];
  const callername = sanitizeE911Field(address.callername);
  const address1 = sanitizeE911Field(address.address1);
  const address2 = sanitizeE911Field(address.address2);
  const city = sanitizeE911Field(address.city);
  const state = sanitizeE911Field(address.state);
  const zip = sanitizeE911Field(address.zip);
  const country = sanitizeE911Field(address.country);
  const street = [address1, address2].filter(Boolean).join(', ');
  const cityLine = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [callername, street, cityLine, country].filter(Boolean);
};

export const hasUsableDeviceLocation = address =>
  ['address1', 'city', 'state', 'zip'].every(
    field => sanitizeE911Field(address?.[field]).length > 0,
  );

export const extractGeocodeComponents = input => ({
  AddressLine1: input.address1 || input.address || '',
  AddressLine2: (input.address2 || '').trim(),
  City: input.city || '',
  StateCode: input.state || '',
  Zip: input.zip || '',
});

export const getPortAssignedNumbers = (box, selectedPort, { excludeCurrentPort = false, excludeNumber = null } = {}) => {
  const numbers = [];
  (box?.Obis || []).forEach(obi => {
    [obi?.port1, obi?.port2].forEach(port => {
      if (!port?.assignedNumber) return;
      if (
        excludeCurrentPort &&
        selectedPort?.boxPortNumber &&
        port.boxPortNumber === selectedPort.boxPortNumber
      ) {
        return;
      }
      if (excludeNumber && port.assignedNumber === excludeNumber) return;
      numbers.push({
        number: port.assignedNumber,
        _id: port?.assignedNumberRef?._id || port?.assignedNumberRef,
        boxPortNumber: port.boxPortNumber,
      });
    });
  });
  return numbers;
};

/** Line types that require box-level E911 before they can be provisioned. */
export const E911_REQUIRED_LINE_TYPES = ['voice', 'hunt', 'elevator', 'ringDown'];

export const isE911RequiredLineType = type => E911_REQUIRED_LINE_TYPES.includes(type);

/**
 * Prompt for E911 when provisioning a voice-class line and the box has no E911 yet.
 * Non-voice types (fax, modem, elevator, etc.) can be provisioned without E911.
 */
export const shouldPromptE911Address = (box, _selectedPort, lineType) => {
  if (box?.myEpik || (box?.e911Number && `${box.e911Number}`.trim())) return false;
  return isE911RequiredLineType(lineType);
};

export const shouldPromptE911Reassign = (box, selectedPort, suggestion) => {
  if (box?.myEpik || !box?.e911Number) return false;
  if (!suggestion?.number || suggestion.number === box.e911Number) return false;

  const currentPortNumber =
    selectedPort?.assignedNumber || selectedPort?.assignedNumberRef?.number || '';
  const replacingOnThisPort = !!currentPortNumber && currentPortNumber === box.e911Number;
  const e911StillOnOtherPorts = getPortAssignedNumbers(box, selectedPort, {
    excludeCurrentPort: true,
  }).some(item => item.number === box.e911Number);
  const e911Orphaned = !currentPortNumber && !e911StillOnOtherPorts;

  return replacingOnThisPort || e911Orphaned;
};

export const hasValidE911ForIntegration = box => {
  if (box?.myEpik) return true;
  if (!box?.e911Number) return false;
  return ['address1', 'city', 'state', 'zip'].every(
    field => sanitizeE911Field(box?.e911Info?.[field]).length > 0,
  );
};

export const getEligibleE911Numbers = (
  box,
  selectedPort,
  pendingNumber,
  assignedNumbers = [],
  excludeNumber = null,
) =>
  getPortAssignedNumbers(box, selectedPort, {
    excludeCurrentPort: true,
    excludeNumber,
  })
    .concat(pendingNumber ? [pendingNumber] : [])
    .filter((item, index, self) => item?.number && self.findIndex(n => n.number === item.number) === index)
    .map(item => assignedNumbers.find(n => n.number === item.number) || item);
